import {computed, Injector, signal, Signal} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, of, Subject} from 'rxjs';
import {DexComputedLpService} from '../../components/dex/dex-streaming/computed/dex-computed-lp.service';
import {DexComputedTrancheService} from '../../components/dex/dex-streaming/computed/dex-computed-tranche.service';
import {ComputedTranche} from '../../components/dex/dex-streaming/types/computed/computed-tranche';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {
    getWidgetSettingsWithUpdatesStream,
    hasSettingUpdateField,
    pipeSettingsCorrections,
    setWidgetSettings,
    updateWidgetSettings
} from '../components/common-widget.implementation';
import {DexLpDetailsFeature, DexLpDetailsFeatures} from './settings/dex-lp-details-feature';
import {DexLpDetailsWidgetSettings} from './settings/dex-lp-details-widget-settings';
import {DexLpDetailsWidgetSettingsService} from './settings/dex-lp-details-widget-settings.service';

export class DexLpDetailsWidgetImplementation implements WidgetImplementation {

    public settings: DexLpDetailsWidgetSettings;

    public settingsService: DexLpDetailsWidgetSettingsService = this.injector.get(DexLpDetailsWidgetSettingsService);

    public ready$ = new BehaviorSubject<boolean>(false);

    public event$ = new Subject<WidgetImplementationEvent>();

    public readonly $features = signal<DexLpDetailsFeatures>({});

    public settingsWithUpdates$: Observable<Partial<DexLpDetailsWidgetSettings>> =
               getWidgetSettingsWithUpdatesStream(this);

    public readonly $lp = computed(() => this.dexComputedLpService.$list()[0]);
    public readonly $traches: Signal<ComputedTranche[]>;

    private readonly dexComputedTrancheService = this.injector.get(DexComputedTrancheService);
    private readonly dexComputedLpService = this.injector.get(DexComputedLpService);

    constructor(
        private injector: Injector
    ) {
        this.$traches = this.dexComputedTrancheService.$list;
    }

    public setSettings(
        settings?: DexLpDetailsWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<DexLpDetailsWidgetSettings> {
        return setWidgetSettings(this, settings, flags);
    }

    public updateSettings(
        updates?: Partial<DexLpDetailsWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<DexLpDetailsWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public resolveSettings(
        settings: Partial<DexLpDetailsWidgetSettings>
    ): Observable<Partial<DexLpDetailsWidgetSettings>> {
        return pipeSettingsCorrections<DexLpDetailsWidgetSettings>([
            s => this.resolveSettingsFeatures(s)
        ])(settings, this);
    }

    public toggleFeatureCollapsed(newState?: boolean): Observable<Partial<DexLpDetailsWidgetSettings>> {
        const oldState = !!this.$features().COLLAPSED;
        newState = newState === undefined ? !oldState : newState;

        let result$: Observable<Partial<DexLpDetailsWidgetSettings>> = EMPTY;

        if (newState !== oldState) {
            const newSettingsFeatures: DexLpDetailsFeature[] = oldState
                ? this.settings.features.filter(f => f !== DexLpDetailsFeature.COLLAPSED)
                : [...this.settings.features, DexLpDetailsFeature.COLLAPSED];

            result$ = this.updateSettings({
                features: newSettingsFeatures
            });
        }

        return result$;
    }

    private readSettingsFeatures(
        settings?: Partial<DexLpDetailsWidgetSettings>
    ): void {
        if (settings?.features) {
            if (settings?.features) {
                this.$features.set(
                    Object.fromEntries(settings.features.map(key => [key, true]))
                );
            }
        }
    }

    private resolveSettingsFeatures(
        settingsUpdate: Partial<DexLpDetailsWidgetSettings>
    ): Observable<Partial<DexLpDetailsWidgetSettings>> {
        if (hasSettingUpdateField(settingsUpdate, 'features')) {
            this.readSettingsFeatures(settingsUpdate);
        }

        return of(undefined);
    }

}
